import {FC} from 'react';
import {Calendar} from 'lucide-react';
import {Link} from '@tanstack/react-router';
import {formatEventDate} from '@/utils/displayFormatUtils';
import {Discipline, EventType, type MapEvent} from '@/apiTypes/event';
import {EventTypeBadge} from '../eventTypeBadge/eventTypeBadge';
import {Pill, PillVariant} from '../ui/pill';
import {Typography} from '../ui/typography';
import {palette} from '@/styles/colors';
import {Card} from '../ui/card';
import {cn} from '@/lib/utils';
import {formatLocationShort} from '@/utils/formatLocationShort/formatLocationShort';
import {FeaturedBadge} from '../featuredBadge/featuredBadge';

interface MobileEventDetailCardProps {
  event: MapEvent;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

/**
 * A compact event detail card designed for the mobile bottom sheet.
 * Renders a 16:9 hero image with the event name overlaid on a bottom
 * gradient, event type badge top-left, price pill top-right, and a
 * single row of location + date beneath. The entire card is a Link
 * that navigates to the full event detail page.
 */
export const MobileEventDetailCard: FC<MobileEventDetailCardProps> = ({
  event,
  onMouseEnter,
  onMouseLeave,
}) => {
  const formattedDate = formatEventDate(event.startDate, event.endDate);
  const hasImage = event.heroUrl;

  return (
    <Link
      to="/event/$eventId"
      params={{eventId: event.id}}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Card className={cn(
        "flex flex-row p-0 gap-3 rounded-lg bg-white overflow-hidden",
        event.editorialContent?.trim() ? "border border-primary-500" : "",
      )}>
        {/* Hero image with overlaid title */}
        {hasImage && (
          <div className="relative w-28 overflow-hidden">
            <img
              src={event.heroUrl}
              alt={`${event.name} hero image`}
              className="w-full h-full object-cover"
            />

            <div className="absolute bottom-2 left-3 right-2 flex flex-col gap-1.5">
              <MetaBadge
                discipline={event.discipline}
                eventType={event.eventType}
              />
              {event.editorialContent?.trim() && (
                <FeaturedBadge />
              )}
            </div>
          </div>
        )}

        {/* Location & date side-by-side */}
        <div className="flex flex-1 flex-col p-3 gap-2">
          <div>
            <div className="flex items-start justify-between gap-1">
              <Typography
                level="B2"
                className="text-ellipsis line-clamp-2 h-[2lh] tracking-wide"
              >
                {event.name}
              </Typography>
              {!hasImage && (
                <div className="flex flex-col items-end gap-1.5">
                  <MetaBadge
                    eventType={event.eventType}
                    discipline={event.discipline}
                    variant={{eventType: 'default', discipline: 'ghost'}}
                  />
                  {event.editorialContent?.trim() && (
                    <FeaturedBadge />
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 items-center">
              <Typography level="B3" className="text-neutral-500">
                {formatLocationShort(event.location)}
              </Typography>
            </div>
          </div>
          <div className="flex flex-row gap-1">
            {formattedDate && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Calendar className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                <Typography level="C1" className="text-neutral-400">
                  {formattedDate}
                </Typography>
              </div>
            )}
            <Typography level="C1" className="text-neutral-500">
              {event.location && event.organizer?.name && '·'}
            </Typography>
            <Typography level="C1" className="text-neutral-500">
              {event.organizer?.name}
            </Typography>
          </div>
        </div>
      </Card>
    </Link>
  );
};

type MetaBadgeVariant =
  | PillVariant
  | {eventType?: PillVariant; discipline?: PillVariant};

interface MetaBadgeProps {
  discipline: Discipline;
  eventType: EventType;
  variant?: MetaBadgeVariant;
  className?: string;
}

/**
 * Renders the event discipline and type badges.
 * `variant` accepts either a single PillVariant (applied to both badges)
 * or an object with `eventType` and `discipline` keys to target each individually.
 */
const MetaBadge: FC<MetaBadgeProps> = ({
  discipline,
  eventType,
  className,
  variant = 'overlay',
}) => {
  const eventTypeVariant =
    typeof variant === 'object' ? (variant.eventType ?? 'overlay') : variant;
  const disciplineVariant =
    typeof variant === 'object' ? (variant.discipline ?? 'overlay') : variant;

  return (
    <div className={cn('flex items-start justify-between gap-1', className)}>
      <EventTypeBadge
        variant={eventTypeVariant}
        size="xs"
        eventType={eventType}
        className="text-[10px]"
      />
      <Pill
        variant={disciplineVariant}
        color={palette.neutral[600]}
        size="xs"
        className="text-[10px]"
      >
        {discipline}
      </Pill>
    </div>
  );
};
